// Right-side drawer that shows a single issue's full detail — file:line
// "code excerpt" region plus the matching rule's remediation hint (T033).
//
// Why a native <dialog> styled as a side drawer:
//   - Free focus trap, Esc-to-close, backdrop-click-to-close. Matches the
//     pattern Projects.tsx already uses for `NewProjectDialog`, so behavior
//     is consistent across the FE.
//   - No new dependency. Radix Dialog or react-aria would buy us slightly
//     nicer animation primitives, but the cost (~20 KB and a setup-piece
//     each consumer has to know about) isn't worth it for one drawer.
//
// What's in the drawer:
//   - Header strip: severity chip + kind chip + "new" pip + close button.
//   - Title: the analyzer's `message` (often the most actionable line).
//   - Meta grid: file:line range, rule id, detected_at, resolution state,
//     CWE/OWASP tags, estimated remediation effort.
//   - "Source preview" panel: shows `file_path` + line range. The actual
//     source bytes aren't in the DB today — the scan bundle lives in S3
//     and there's no API yet to extract a windowed excerpt. Rather than
//     fake a snippet, we render an honest placeholder so the panel's
//     position is settled and a future ticket (source-excerpt endpoint)
//     just swaps in real content.
//   - "Remediation hint" panel: pulled from the rule catalog at
//     /api/v1/rules. The rule's `description` is short markdown — we
//     render paragraphs + inline `code` without pulling in a markdown
//     library (the catalog descriptions are deliberately constrained).
//
// URL state: the parent (Issues.tsx) drives `?issue=<id>` in the URL.
// This drawer is a controlled component over that prop, which makes the
// drawer shareable via deep link and lets browser back/forward dismiss it.
//
// Deep-link fallback: when `?issue=<id>` references an issue that isn't in
// the currently-loaded page (filters changed, hit the end of paging, etc.),
// the drawer renders a "not in current view" notice. The Phase-1 backend
// doesn't expose GET /api/v1/issues/{id}; once it does, this fallback can
// fetch it directly.

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import {
  AlertTriangle,
  ArrowRight,
  Bug,
  CircleSlash,
  Copy,
  Eye,
  FileCode2,
  Info,
  KeyRound,
  Lightbulb,
  Loader2,
  ShieldAlert,
  Sparkles,
  Sprout,
  Wand2,
  Wrench,
  X,
} from 'lucide-react';
import {
  ApiError,
  FlowStep,
  Issue,
  IssueKind,
  IssueSeverity,
  JiraLink,
  RULES_CATALOG_URL,
  FixVerification,
  FixVerificationStatus,
  Rule,
  RuleListResponse,
  fetcher,
  jiraApi,
  llmApi,
} from '../lib/api';
import { parseScaMessage } from '../lib/scaMessage';
import { safeHref } from '../lib/safeHref'

// ---------------------------------------------------------------------------
// Tone tables — kept in sync with the chips on Issues.tsx so a row's
// severity chip and the drawer's severity chip read identically.
// ---------------------------------------------------------------------------

const SEVERITY_TONE: Record<IssueSeverity, string> = {
  INFO: 'bg-bg-inset text-fg-muted border-border',
  MINOR: 'bg-accent-muted text-accent border-accent/30',
  MAJOR: 'bg-warning/10 text-warning border-warning/40',
  CRITICAL: 'bg-danger/10 text-danger border-danger/40',
  BLOCKER: 'bg-danger/20 text-danger border-danger/60',
};

const KIND_LABEL: Record<IssueKind, string> = {
  BUG: 'Bug',
  CODE_SMELL: 'Smell',
  VULNERABILITY: 'Vuln',
  SECURITY_HOTSPOT: 'Hotspot',
  SECRET: 'Secret',
};

const KIND_ICON: Record<IssueKind, JSX.Element> = {
  BUG: <Bug className="h-3 w-3" aria-hidden="true" />,
  CODE_SMELL: <Sprout className="h-3 w-3" aria-hidden="true" />,
  VULNERABILITY: <ShieldAlert className="h-3 w-3" aria-hidden="true" />,
  SECURITY_HOTSPOT: <Eye className="h-3 w-3" aria-hidden="true" />,
  SECRET: <KeyRound className="h-3 w-3" aria-hidden="true" />,
};

const SEVERITIES = new Set<IssueSeverity>([
  'INFO',
  'MINOR',
  'MAJOR',
  'CRITICAL',
  'BLOCKER',
]);
const KINDS = new Set<IssueKind>([
  'BUG',
  'CODE_SMELL',
  'VULNERABILITY',
  'SECURITY_HOTSPOT',
  'SECRET',
]);

function asSeverity(raw: string): IssueSeverity | undefined {
  const up = raw.toUpperCase() as IssueSeverity;
  return SEVERITIES.has(up) ? up : undefined;
}
function asKind(raw: string): IssueKind | undefined {
  const up = raw.toUpperCase() as IssueKind;
  return KINDS.has(up) ? up : undefined;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface IssueDetailDrawerProps {
  /** Selected issue, or null when no issue is selected. The drawer is
   *  controlled — the parent decides what to show; the drawer just renders
   *  it and reports `onClose` when the user dismisses it. */
  readonly issue: Issue | null;
  /** Issue ID the URL is asking for, used only when `issue` is null so we
   *  can show a "not in current view" message that still references the
   *  requested id. */
  readonly requestedID?: string;
  /** Called when the user dismisses the drawer (Esc, backdrop, or button). */
  readonly onClose: () => void;
}

export function IssueDetailDrawer({
  issue,
  requestedID,
  onClose,
}: IssueDetailDrawerProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Open/close `dialog` imperatively so we don't fight React's render flow.
  // showModal() is the only way to get the native focus trap + Esc + backdrop
  // semantics; close() emits the `close` event that wires back to onClose.
  const shouldOpen = issue !== null || (requestedID !== undefined && requestedID !== '');
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (shouldOpen && !d.open) d.showModal();
    if (!shouldOpen && d.open) d.close();
  }, [shouldOpen]);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Issue detail"
      className="
        bg-transparent p-0 m-0
        max-w-none max-h-none w-full h-full
        backdrop:bg-bg-base/60 backdrop:backdrop-blur-sm
      "
    >
      {/* The visible panel sits flush to the right of the viewport.  We rely
          on flex-row + ml-auto to position rather than fixed/absolute so
          the native <dialog> default centering doesn't fight us. */}
      <div className="h-full w-full flex">
        {/* Backdrop-click dismissal: transparent button covers the empty left
            area so click-outside-to-close works without putting onClick on the
            <dialog> element itself (which triggers S6847/S1082). */}
        <button
          type="button"
          onClick={onClose}
          aria-hidden="true"
          tabIndex={-1}
          className="flex-1 h-full cursor-default bg-transparent border-none"
        />
        <div className="h-full w-full max-w-xl bg-bg-elevated border-l border-border shadow-2xl overflow-y-auto">
          {issue ? (
            <DrawerBody key={issue.id} issue={issue} onClose={onClose} />
          ) : (
            <DrawerMissing requestedID={requestedID} onClose={onClose} />
          )}
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function DrawerBody({ issue, onClose }: { readonly issue: Issue; readonly onClose: () => void }) {
  const severity = asSeverity(issue.severity);
  const kind = asKind(issue.kind);
  const severityTone = severity
    ? SEVERITY_TONE[severity]
    : SEVERITY_TONE.INFO;
  const fileRange = useMemo(() => formatFileRange(issue), [issue]);

  // Catalog fetch — SWR de-dupes the request across drawer opens.
  // revalidateOnFocus is off because the catalog is static per server build.
  const catalog = useSWR<RuleListResponse>(
    RULES_CATALOG_URL,
    fetcher,
    { revalidateOnFocus: false },
  );
  const rule = useMemo(
    () => catalog.data?.items.find((r) => r.id === issue.rule_id),
    [catalog.data, issue.rule_id],
  );

  return (
    <div className="flex flex-col h-full">
      <Header issue={issue} severity={severity} severityTone={severityTone} kind={kind} onClose={onClose} />

      <div className="px-6 py-5 space-y-6 flex-1">
        <Title message={issue.message} />

        <MetaGrid issue={issue} fileRange={fileRange} />

        <CodeExcerptPanel issue={issue} fileRange={fileRange} />

        {(issue.flow?.length ?? 0) > 0 ? (
          <DataflowPanel flow={issue.flow!} />
        ) : null}

        <RemediationPanel
          ruleID={issue.rule_id}
          rule={rule}
          catalogLoading={catalog.isLoading}
          catalogError={catalog.error}
        />

        {issue.rule_id?.startsWith('sca:') || issue.rule_id === 'container:vuln' ? (
          <ScaAdvisoryPanel message={issue.message} />
        ) : null}

        <SuggestFixPanel issue={issue} />

        <JiraPanel issue={issue} />

        <TagsRow issue={issue} />

        <CleanCodeTaxonomyRow issue={issue} />

        <ContextNote issue={issue} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header strip — sticky so the close button stays reachable on long content.
// ---------------------------------------------------------------------------

function Header({
  issue,
  severity,
  severityTone,
  kind,
  onClose,
}: {
  readonly issue: Issue;
  readonly severity: IssueSeverity | undefined;
  readonly severityTone: string;
  readonly kind: IssueKind | undefined;
  readonly onClose: () => void;
}) {
  return (
    <header
      className="
        sticky top-0 z-10 bg-bg-elevated/95 backdrop-blur-sm
        border-b border-border px-6 py-4
        flex items-center gap-3
      "
    >
      <span className={`orb-badge border shrink-0 ${severityTone}`}>
        {severity ?? issue.severity ?? 'INFO'}
      </span>
      {kind ? (
        <span
          className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0"
          title={kind.replace('_', ' ').toLowerCase()}
        >
          {KIND_ICON[kind]}
          <span>{KIND_LABEL[kind]}</span>
        </span>
      ) : null}
      {issue.is_new ? (
        <span
          className="orb-badge border bg-accent/15 text-accent border-accent/30 shrink-0"
          title="Introduced in the current branch"
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          <span>new</span>
        </span>
      ) : null}
      <span className="flex-1" />
      <button
        type="button"
        onClick={onClose}
        aria-label="Close issue detail"
        className="
          inline-flex items-center justify-center h-8 w-8 rounded-md
          text-fg-muted hover:text-fg hover:bg-bg-hover
          border border-transparent hover:border-border transition-colors
        "
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Title — the analyzer's message, rendered with display font so it doesn't
// get visually lost between the badge row and the meta grid.
// ---------------------------------------------------------------------------

function Title({ message }: { readonly message: string }) {
  return (
    <div>
      <p className="orb-eyebrow mb-1">finding</p>
      <h2 className="font-display text-lg font-semibold text-fg leading-snug">
        {message || <span className="text-fg-subtle">(no message)</span>}
      </h2>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meta grid
// ---------------------------------------------------------------------------

function MetaGrid({ issue, fileRange }: { readonly issue: Issue; readonly fileRange: string }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
      <MetaRow label="Location">
        <span className="orb-code">{fileRange}</span>
      </MetaRow>
      {issue.rule_id ? (
        <MetaRow label="Rule">
          <span className="orb-code text-fg-muted">{issue.rule_id}</span>
        </MetaRow>
      ) : null}
      {issue.detected_at ? (
        <MetaRow label="Detected">
          <span>{formatTimestamp(issue.detected_at)}</span>
        </MetaRow>
      ) : null}
      <MetaRow label="Effort">
        <span>{issue.effort_min ? `${issue.effort_min} min` : '—'}</span>
      </MetaRow>
      <MetaRow label="Resolution">
        <ResolutionBadge resolution={issue.resolution} />
      </MetaRow>
    </dl>
  );
}

function MetaRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <>
      <dt className="orb-section-title self-center">{label}</dt>
      <dd className="text-fg break-words min-w-0">{children}</dd>
    </>
  );
}

function ResolutionBadge({ resolution }: { readonly resolution: string }) {
  if (!resolution) {
    return (
      <span className="inline-flex items-center gap-1.5 text-fg-muted">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Open</span>
      </span>
    );
  }
  let label: string;
  if (resolution === 'fixed') {
    label = 'Fixed';
  } else if (resolution === 'wontfix') {
    label = "Won't fix";
  } else if (resolution === 'falsepositive') {
    label = 'False positive';
  } else {
    label = resolution;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-muted">
      <CircleSlash className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Source preview panel
// ---------------------------------------------------------------------------

function CodeExcerptPanel({
  issue,
  fileRange,
}: {
  readonly issue: Issue;
  readonly fileRange: string;
}) {
  // line_start of 0 means the analyzer didn't pin a location (e.g. a
  // file-level rule fires on the whole file). Show the file alone in that
  // case so the panel is still useful.
  const hasLine = (issue.line_start ?? 0) > 0;
  return (
    <section aria-label="Source preview" className="orb-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileCode2
          className="h-3.5 w-3.5 text-fg-muted"
          aria-hidden="true"
        />
        <span className="orb-section-title">Source preview</span>
      </div>
      <div
        className="
          rounded-md border border-border bg-bg-inset
          font-mono text-xs text-fg-muted
          px-4 py-3
        "
      >
        <p className="text-fg">
          <span className="text-fg-subtle">file</span>{' '}
          <span className="text-fg">{issue.file_path || '—'}</span>
        </p>
        {hasLine ? (
          <p className="mt-1">
            <span className="text-fg-subtle">range</span>{' '}
            <span className="text-fg">{fileRange.split(':').slice(1).join(':') || '—'}</span>
          </p>
        ) : null}
        <p className="mt-3 text-fg-subtle leading-relaxed">
          Source excerpts aren&apos;t fetched in Phase&nbsp;1 — the scan
          bundle lives in object storage and a windowed source endpoint
          arrives in a follow-up ticket. Until then, jump to{' '}
          <span className="orb-code">{fileRange}</span> in your editor or
          repo browser.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dataflow path panel — T211
//
// Renders the ordered source→sink chain emitted by taint analyzers.
// Each step shows the file:line location and its role (source/sink).
// Missing for non-taint rules (flow is absent or empty).
// ---------------------------------------------------------------------------

const FLOW_DESC_LABEL: Record<string, string> = {
  source: 'Source',
  propagation: 'Propagation',
  sink: 'Sink',
};

const FLOW_DESC_TONE: Record<string, string> = {
  source: 'text-warning',
  propagation: 'text-fg-muted',
  sink: 'text-danger',
};

function DataflowPanel({ flow }: { readonly flow: FlowStep[] }) {
  return (
    <section aria-label="Dataflow path" className="orb-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRight className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
        <span className="orb-section-title">Dataflow path</span>
        <span className="ml-auto text-xs text-fg-subtle">{flow.length} step{flow.length === 1 ? '' : 's'}</span>
      </div>
      <ol className="space-y-3">
        {flow.map((step, idx) => {
          let desc: string;
          if (step.desc) {
            desc = step.desc;
          } else if (idx === 0) {
            desc = 'source';
          } else if (idx === flow.length - 1) {
            desc = 'sink';
          } else {
            desc = 'propagation';
          }
          const label = FLOW_DESC_LABEL[desc] ?? desc;
          const tone = FLOW_DESC_TONE[desc] ?? 'text-fg-muted';
          const location = step.line > 0
            ? `${step.path}:${step.line}`
            : step.path;
          const prevPath = idx > 0 ? flow[idx - 1].path : null;
          const crossFile = prevPath !== null && prevPath !== step.path;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li key={idx} className="text-xs space-y-1.5">
              {/* Cross-file transition marker (T219) */}
              {crossFile && (
                <div className="flex items-center gap-1.5 text-fg-subtle py-0.5 border-t border-dashed border-border/60 pt-2">
                  <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="italic truncate">crosses to {step.path}</span>
                </div>
              )}
              <div className="flex items-start gap-3">
                <span className={`shrink-0 font-medium w-20 pt-0.5 ${tone}`}>{label}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="orb-code text-fg-muted break-all">{location}</span>
                    {/* Function name badge (T219) */}
                    {step.func_name ? (
                      <span className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0">
                        {step.func_name}
                      </span>
                    ) : null}
                  </div>
                  {/* Per-node code excerpt (T219) */}
                  {step.snippet ? (
                    <pre className="mt-1.5 px-2 py-1 rounded bg-bg-inset border border-border font-mono text-[10px] text-fg overflow-x-auto whitespace-pre leading-relaxed">
                      {step.snippet}
                    </pre>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Remediation hint
// ---------------------------------------------------------------------------

function RemediationPanel({
  ruleID,
  rule,
  catalogLoading,
  catalogError,
}: {
  readonly ruleID: string;
  readonly rule: Rule | undefined;
  readonly catalogLoading: boolean;
  readonly catalogError: unknown;
}) {
  let remediationContent: ReactNode;
  if (catalogLoading) {
    remediationContent = (
      <p className="inline-flex items-center gap-2 text-sm text-fg-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading rule catalog…
      </p>
    );
  } else if (catalogError) {
    remediationContent = <RemediationError error={catalogError} />;
  } else if (rule) {
    remediationContent = (
      <>
        <Markdown text={rule.description} />
        <div className="mt-3 pt-3 border-t border-border">
          <Link
            to={`/rules?rule=${encodeURIComponent(rule.id)}`}
            className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          >
            View full rule details
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </>
    );
  } else {
    remediationContent = <RemediationMissing ruleID={ruleID} />;
  }

  return (
    <section aria-label="Remediation hint" className="orb-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
        <span className="orb-section-title">Remediation hint</span>
        {rule ? (
          <span className="ml-auto text-xs text-fg-subtle">
            from <span className="orb-code">{rule.id}</span>
          </span>
        ) : null}
      </div>

      {remediationContent}
    </section>
  );
}

function RemediationError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'unknown error';
  }
  return (
    <div className="text-sm text-fg-muted">
      <p className="inline-flex items-center gap-2 text-danger">
        <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
        Couldn&apos;t load the rule catalog.
      </p>
      <p className="mt-1 text-xs text-fg-subtle">{msg}</p>
    </div>
  );
}

function RemediationMissing({ ruleID }: { readonly ruleID: string }) {
  return (
    <p className="text-sm text-fg-muted">
      Rule <span className="orb-code">{ruleID || '—'}</span> isn&apos;t in
      the active catalog. This usually means the analyzer that emitted it
      was removed since the scan ran, or a custom rule is in play. Check
      the rules catalog for the rule&apos;s current state.
    </p>
  );
}

// ---------------------------------------------------------------------------
// AI-Remediation panel (T180c)
//
// Idle    → single "Suggest fix" button.
// Loading → button shows spinner and is disabled while the LLM call runs.
// Done    → diff block (plain <pre>) + "Copy patch" + "Dismiss" buttons.
// Error   → 429/503 show a muted informational box (budget/config message,
//           not an error toast); all other errors show a compact error note.
// ---------------------------------------------------------------------------

type FixState = 'idle' | 'loading' | 'done' | 'error';

interface FixErrorInfo {
  status: number;
  code: string;
  message: string;
}

const VERIFICATION_LABEL: Record<FixVerificationStatus, string> = {
  verified: 'Verified: finding gone, nothing new',
  verified_with_side_effect: 'Verified with side effect: finding gone, new finding introduced',
  failed: 'Failed',
  unverifiable: 'Not verified: no source excerpt to re-analyse',
};

function FixVerificationBadge({ verification }: { readonly verification: FixVerification | null }) {
  if (!verification) return null;
  return (
    <div data-testid="fix-verification" data-status={verification.status} className="text-xs">
      <span className="font-medium">{VERIFICATION_LABEL[verification.status]}</span>
      {verification.new_findings?.map((f) => (
        <div key={`${f.rule_id}:${f.line}`} className="text-fg-muted">
          new: {f.rule_id} (line {f.line}) — {f.message}
        </div>
      ))}
    </div>
  );
}

function SuggestFixPanel({ issue }: { readonly issue: Issue }) {
  const [fixState, setFixState] = useState<FixState>('idle');
  const [diff, setDiff] = useState('');
  const [verification, setVerification] = useState<FixVerification | null>(null);
  const [fixError, setFixError] = useState<FixErrorInfo | null>(null);
  const [copied, setCopied] = useState(false);

  const handleRequest = useCallback(async () => {
    setFixState('loading');
    setFixError(null);
    try {
      const result = await llmApi.suggestFix(issue);
      setDiff(result.diff);
      setVerification(result.fix?.verification ?? null);
      setFixState('done');
    } catch (err) {
      if (err instanceof ApiError) {
        setFixError({ status: err.status, code: err.code, message: err.message });
      } else {
        setFixError({ status: 0, code: 'unknown', message: String(err) });
      }
      setFixState('error');
    }
  }, [issue]);

  const handleDismiss = useCallback(() => {
    setFixState('idle');
    setDiff('');
    setVerification(null);
    setFixError(null);
    setCopied(false);
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(diff).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [diff]);

  return (
    <section aria-label="AI suggested fix" className="orb-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Wand2 className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
        <span className="orb-section-title">AI suggested fix</span>
        <span className="ml-auto text-xs text-fg-subtle italic">beta</span>
      </div>

      {fixState === 'idle' && (
        <button
          type="button"
          onClick={handleRequest}
          className="
            inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium
            bg-accent/10 text-accent border border-accent/30
            hover:bg-accent/20 hover:border-accent/50 transition-colors
          "
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          Suggest fix
        </button>
      )}

      {fixState === 'loading' && (
        <button
          type="button"
          disabled
          className="
            inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium
            bg-accent/10 text-accent/60 border border-accent/20 cursor-not-allowed
          "
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Generating fix…
        </button>
      )}

      {fixState === 'done' && verification?.status === 'failed' && (
        <p role="status" className="text-sm text-fg-muted">
          The suggested fix did not pass orbital-lint&apos;s own check
          ({verification.reason ?? 'finding still present'}) and is not shown.
        </p>
      )}

      {fixState === 'done' && verification?.status !== 'failed' && (
        <div className="space-y-3">
          <FixVerificationBadge verification={verification} />
          <pre
            className="
              rounded-md border border-border bg-bg-inset
              font-mono text-xs text-fg overflow-x-auto
              px-4 py-3 whitespace-pre leading-relaxed max-h-80
            "
          >
            {diff || '(no changes suggested)'}
          </pre>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                bg-bg-hover text-fg border border-border
                hover:bg-bg-inset transition-colors
              "
            >
              <Copy className="h-3 w-3" aria-hidden="true" />
              {copied ? 'Copied!' : 'Copy patch'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
                text-fg-muted hover:text-fg hover:bg-bg-hover border border-transparent
                hover:border-border transition-colors
              "
            >
              <X className="h-3 w-3" aria-hidden="true" />
              Dismiss
            </button>
          </div>
        </div>
      )}

      {fixState === 'error' && fixError && (() => {
        const isSoft = fixError.status === 429 || fixError.status === 503;
        if (isSoft) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let softMsg: any;
          if (fixError.status === 429) {
            softMsg = 'Fix suggestion unavailable — daily budget or rate limit reached.';
          } else if (fixError.code === 'feature_disabled') {
            softMsg = (
              <>
                AI Remediation is disabled for this organisation.{' '}
                <a href="/admin/features" className="underline underline-offset-2 hover:text-fg">
                  Enable it in Admin → Features
                </a>.
              </>
            );
          } else {
            softMsg = 'Fix suggestion unavailable — the AI service is not configured for this instance.';
          }
          return (
            <div className="flex items-start gap-2 rounded-md bg-bg-inset border border-border px-3 py-2.5 text-sm text-fg-muted">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-fg-subtle" aria-hidden="true" />
              <div className="space-y-1">
                <p>{softMsg}</p>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="text-xs text-fg-subtle hover:text-fg underline underline-offset-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        }
        return (
          <div className="space-y-2">
            <p className="inline-flex items-center gap-2 text-sm text-danger">
              <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
              Couldn&apos;t generate a fix suggestion.
            </p>
            <p className="text-xs text-fg-subtle">{fixError.message}</p>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-xs text-fg-subtle hover:text-fg underline underline-offset-2"
            >
              Dismiss
            </button>
          </div>
        );
      })()}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SCA Advisory panel (T213) — shown when issue.rule_id starts with "sca:".
// Parses the structured message emitted by the SCA analyzer and renders:
//   - Advisory ID with a link to osv.dev (or NVD for CVEs)
//   - Fixed version (upgrade path)
//   - CVSS score badge
//   - Link to the project SBOM for full dependency inventory (T187)
// ---------------------------------------------------------------------------

function cvssToneClass(cvssNum: number | null): string {
  if (cvssNum == null) return 'bg-bg-inset text-fg-muted border-border';
  if (cvssNum >= 9.0) return 'bg-danger/20 text-danger border-danger/60';
  if (cvssNum >= 7.0) return 'bg-danger/10 text-danger border-danger/40';
  if (cvssNum >= 4.0) return 'bg-warning/10 text-warning border-warning/40';
  return 'bg-bg-inset text-fg-muted border-border';
}

function ScaAdvisoryPanel({ message }: { readonly message: string }) {
  const info = parseScaMessage(message);
  // T648: sca:image-base-unknown carries neither an advisory nor a fix (it's
  // an "we couldn't tell what this image is" note), but still has a base
  // image worth surfacing — so baseImage alone is enough to render the panel.
  if (!info.advisoryId && !info.fixedVersion && !info.baseImage) return null;

  const cvssNum = info.cvssScore ? parseFloat(info.cvssScore) : null;
  const cvssTone = cvssToneClass(cvssNum);

  return (
    <section aria-label="Advisory detail" className="space-y-3">
      <p className="orb-section-title">Advisory detail</p>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
        {info.advisoryId ? (
          <>
            <dt className="text-fg-muted font-medium">Advisory</dt>
            <dd>
              {info.advisoryUrl ? (
                <a
                  href={safeHref(info.advisoryUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="orb-code text-accent hover:underline underline-offset-2 inline-flex items-center gap-1"
                >
                  {info.advisoryId}
                  <ArrowRight className="h-3 w-3 rotate-[-45deg]" aria-hidden="true" />
                </a>
              ) : (
                <span className="orb-code">{info.advisoryId}</span>
              )}
              {info.cvssScore ? (
                <span className={`ml-2 orb-badge border ${cvssTone}`}>
                  CVSS {info.cvssScore}
                </span>
              ) : null}
            </dd>
          </>
        ) : null}

        {info.baseImage ? (
          <>
            <dt className="text-fg-muted font-medium">Base image</dt>
            <dd>
              <span className="orb-code">{info.baseImage}</span>
            </dd>
          </>
        ) : null}

        {info.packageName ? (
          <>
            <dt className="text-fg-muted font-medium">Package</dt>
            <dd>
              <span className="orb-code">
                {info.ecosystem ? `${info.ecosystem} ` : ''}{info.packageName}
                {info.installedVersion ? `@${info.installedVersion}` : ''}
              </span>
            </dd>
          </>
        ) : null}

        {info.fixedVersion ? (
          <>
            <dt className="text-fg-muted font-medium">Fix</dt>
            <dd>
              Upgrade to{' '}
              <span className="orb-code text-accent">{info.fixedVersion}</span>
            </dd>
          </>
        ) : null}
      </dl>

      <p className="text-xs text-fg-subtle">
        Full dependency inventory:{' '}
        <a href="/admin/sbom?component=source" className="underline underline-offset-2 hover:text-fg-muted">
          Admin → SBOM
        </a>
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Jira panel (T195a) — "Create Jira ticket" button + link badge.
// ---------------------------------------------------------------------------

function JiraPanel({ issue }: { readonly issue: Issue }) {
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState<JiraLink | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  // Check for an existing Jira link on first render.
  useEffect(() => {
    if (checked) return;
    setChecked(true);
    jiraApi.getLink(issue.id).then((l) => {
      if (l) setLink(l);
    }).catch(() => {/* config absent — silently ignore */});
  }, [issue.id, checked]);

  const handleCreate = async () => {
    setCreating(true);
    setErr(null);
    try {
      const result = await jiraApi.createIssue(issue.id, {
        summary: `[orbital-lint] ${issue.severity}: ${issue.message}`,
        description: `File: ${issue.file_path ?? ''}${issue.line_start == null ? '' : `:${issue.line_start}`}\nRule: ${issue.rule_id}\nSeverity: ${issue.severity}\n\nFound by orbital-lint.`,
      });
      setLink(result);
    } catch (e) {
      if (e instanceof ApiError && e.status === 412) {
        setErr('No Jira config. Set it up at Admin → Integrations.');
      } else if (e instanceof ApiError && e.status === 402) {
        setErr('Jira integration requires a Trial or Enterprise license.');
      } else {
        setErr(e instanceof Error ? e.message : 'failed to create Jira ticket');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <section aria-label="Jira" className="space-y-2">
      <p className="orb-section-title">Jira</p>
      {link ? (
        <a
          href={safeHref(link.jira_issue_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-accent/10 text-accent border border-accent/30 hover:bg-accent/20 transition-colors"
        >
          {link.jira_issue_key} ↗
        </a>
      ) : (
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-border text-fg-muted hover:text-fg hover:border-fg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {creating ? (
            <><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Creating…</>
          ) : (
            'Create Jira ticket'
          )}
        </button>
      )}
      {err && (
        <p className="text-xs text-danger">{err}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tags row — CWE / OWASP. Kept compact so it tucks under the remediation
// panel without competing for attention.
// ---------------------------------------------------------------------------

function TagsRow({ issue }: { readonly issue: Issue }) {
  const cwes = issue.cwe ?? [];
  const owasps = issue.owasp ?? [];
  if (cwes.length === 0 && owasps.length === 0) return null;
  return (
    <section aria-label="Classifications" className="space-y-2">
      {cwes.length > 0 ? (
        <TagGroup label="CWE">
          {cwes.map((c) => (
            <span key={c} className="orb-badge border bg-bg-inset text-fg-muted border-border">
              CWE-{c}
            </span>
          ))}
        </TagGroup>
      ) : null}
      {owasps.length > 0 ? (
        <TagGroup label="OWASP">
          {owasps.map((o) => (
            <span key={o} className="orb-badge border bg-bg-inset text-fg-muted border-border">
              {o}
            </span>
          ))}
        </TagGroup>
      ) : null}
    </section>
  );
}

function TagGroup({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="orb-section-title">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clean Code taxonomy badges — software quality + clean-code attribute (T253).
// Rendered when the issue carries the T253 classification fields.
// ---------------------------------------------------------------------------

const SOFTWARE_QUALITY_TONE: Record<string, string> = {
  Security: 'bg-danger/10 text-danger border-danger/40',
  Reliability: 'bg-warning/10 text-warning border-warning/40',
  Maintainability: 'bg-accent/10 text-accent border-accent/40',
};

const CLEAN_CODE_ATTR_TONE: Record<string, string> = {
  Consistency: 'bg-nebula/15 text-nebula border-nebula/40',
  Intentionality: 'bg-accent/15 text-accent border-accent/40',
  Adaptability: 'bg-warning/10 text-warning border-warning/40',
  Responsibility: 'bg-danger/10 text-danger border-danger/40',
};

/** T9079: says why a finding is INFO when the context gate downgraded it. */
function ContextNote({ issue }: { readonly issue: Issue }) {
  if (!issue.context_tag) return null;
  const cls = issue.context_tag.replace('non-production:', '');
  return (
    <section aria-label="Context" className="space-y-1 text-sm text-fg-muted">
      <p>
        Downgraded to INFO: found in {cls} code, where rule{' '}
        <code>{issue.rule_id}</code> is not a finding
        {issue.context_from ? ` (was ${issue.context_from})` : ''}.
      </p>
    </section>
  );
}

function CleanCodeTaxonomyRow({ issue }: { readonly issue: Issue }) {
  const quality = issue.software_quality;
  const attr = issue.clean_code_attribute;
  if (!quality && !attr) return null;
  return (
    <section aria-label="Clean Code taxonomy" className="space-y-2">
      <TagGroup label="Quality">
        {quality ? (
          <span
            className={`orb-badge border ${SOFTWARE_QUALITY_TONE[quality] ?? 'bg-bg-inset text-fg-muted border-border'}`}
            title="Software quality dimension (T253)"
          >
            {quality}
          </span>
        ) : null}
        {attr ? (
          <span
            className={`orb-badge border ${CLEAN_CODE_ATTR_TONE[attr] ?? 'bg-bg-inset text-fg-muted border-border'}`}
            title="Clean-code attribute (T253)"
          >
            {attr}
          </span>
        ) : null}
      </TagGroup>
    </section>
  );
}

// ---------------------------------------------------------------------------
// "Not in current view" fallback for deep links that referenced an issue we
// don't have loaded.
// ---------------------------------------------------------------------------

function DrawerMissing({
  requestedID,
  onClose,
}: {
  readonly requestedID?: string;
  readonly onClose: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <header
        className="
          sticky top-0 z-10 bg-bg-elevated/95 backdrop-blur-sm
          border-b border-border px-6 py-4
          flex items-center gap-3
        "
      >
        <span className="orb-badge border bg-bg-inset text-fg-muted border-border">
          unavailable
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close issue detail"
          className="
            inline-flex items-center justify-center h-8 w-8 rounded-md
            text-fg-muted hover:text-fg hover:bg-bg-hover
            border border-transparent hover:border-border transition-colors
          "
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>
      <div className="px-6 py-8 text-sm text-fg-muted space-y-3">
        <p className="font-display text-base text-fg">
          Issue not in the current view.
        </p>
        <p>
          The issue you linked to{' '}
          {requestedID ? (
            <>
              (<span className="orb-code">{requestedID}</span>){' '}
            </>
          ) : null}
          isn&apos;t in the filtered list. Loosen the filter chips at the
          top of the page or scroll further if pagination didn&apos;t reach
          it yet.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "path/to/file.go:L12–L18", or "path:L12", or just "path" if no line. */
function formatFileRange(issue: Issue): string {
  const path = issue.file_path || '—';
  const start = issue.line_start ?? 0;
  const end = issue.line_end ?? 0;
  if (!start) return path;
  if (end > start) return `${path}:L${start}–L${end}`;
  return `${path}:L${start}`;
}

/** Same compact-local format as Issues.tsx / ProjectDetail.tsx. */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Lightweight markdown renderer.
//
// The catalog descriptions are short prose with inline `code` spans — we
// don't need a full CommonMark parser. We split on blank lines for
// paragraphs and inline-format backtick-delimited spans. This keeps the
// bundle small and the code review trivial.
//
// If we ever need block-level features (lists, links) we can drop in
// react-markdown then; for now the constrained text shape is by design.
// ---------------------------------------------------------------------------

function Markdown({ text }: { readonly text: string }) {
  const paragraphs = useMemo(() => splitParagraphs(text), [text]);
  if (paragraphs.length === 0) {
    return (
      <p className="text-sm text-fg-muted">
        This rule has no remediation hint recorded.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {paragraphs.map((p) => (
        <p key={p} className="text-sm text-fg leading-relaxed">
          {renderInline(p)}
        </p>
      ))}
    </div>
  );
}

function splitParagraphs(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/** Render inline `code` spans. Plain text otherwise. */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(/`([^`]+)`/g)) {
    const idx = m.index ?? 0;
    if (idx > last) {
      parts.push(text.slice(last, idx));
    }
    parts.push(
      <code
        key={`c-${key++}`}
        className="orb-code text-xs px-1 py-0.5 rounded bg-bg-inset border border-border"
      >
        {m[1]}
      </code>,
    );
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
